import { fetchUnitDomainGraph } from '$lib/api/unit';
import { getErrorMessage } from '$lib/api';
import { router } from '$lib/router/router.svelte';
import { clearError, setError, setLoading } from '$lib/state/ui.svelte';
import { showSuccess, showError } from '$lib/state/toast.svelte';
import { fetchProjectById } from '$lib/queries/cache';
import {
	downloadElectricalLayout as exportElectricalLayoutPdf,
	downloadLoadChart as exportLoadChartPdf,
	downloadLogicSheet as exportLogicSheetPdf
} from '$lib/utils/download';
import { buildExportData } from '$lib/utils/electricalLayoutData';

async function unitContext() {
	const unitId = router.unitId;
	if (!unitId) return null;

	const projectId = router.projectId;
	const [project, domainGraph] = await Promise.all([
		projectId ? fetchProjectById(projectId) : Promise.resolve(null),
		fetchUnitDomainGraph(unitId)
	]);

	return { unitId, project, domainGraph, unit: domainGraph.unit };
}

function resolveProjectName(ctx: Awaited<ReturnType<typeof unitContext>>): { name: string } {
	if (ctx?.project?.name) return { name: ctx.project.name };
	const fromUnit = (ctx?.unit as { project?: unknown } | undefined)?.project;
	if (typeof fromUnit === 'string' && fromUnit.trim()) return { name: fromUnit };
	return { name: 'Project' };
}

export async function downloadLogicSheet() {
	const ctx = await unitContext();
	if (!ctx) return;

	setLoading(true);
	clearError();

	try {
		await exportLogicSheetPdf(ctx.unit.name, ctx.domainGraph);
		showSuccess('Logic sheet downloaded successfully');
	} catch (error) {
		showError(getErrorMessage(error) || 'Failed to generate logic sheet');
	} finally {
		setLoading(false);
	}
}

export async function downloadLoadChart() {
	const ctx = await unitContext();
	if (!ctx) return;

	setLoading(true);
	clearError();

	try {
		await exportLoadChartPdf(ctx.unitId, ctx.unit.name, ctx.domainGraph);
		showSuccess('Load chart downloaded successfully');
	} catch (error) {
		showError(getErrorMessage(error) || 'Failed to generate load chart');
	} finally {
		setLoading(false);
	}
}

export async function exportElectricalLayout() {
	const ctx = await unitContext();
	if (!ctx) return;

	setLoading(true);
	clearError();

	try {
		await exportElectricalLayoutPdf(ctx.unitId, ctx.unit.name, ctx.domainGraph);
		showSuccess('Electrical layout downloaded successfully');
	} catch (error) {
		setError(getErrorMessage(error));
	} finally {
		setLoading(false);
	}
}

export async function downloadEngravingSheet() {
	const ctx = await unitContext();
	if (!ctx) return;

	setLoading(true);
	clearError();
	try {
		const { generateEngravingSheetPdf } = await import('./utils/engravingExport');
		const base = buildExportData(ctx.domainGraph);
		const keysByKeypad = new Map<number, typeof base.keypadKeys>();
		for (const kp of base.keypads) {
			keysByKeypad.set(
				kp.id,
				base.keypadKeys.filter((k) => k.keypad === kp.id)
			);
		}
		const roomsById = new Map(base.rooms.map((r) => [r.id, r.name]));
		const engravingKeypads = base.keypads.map((kp) => ({
			id: kp.id,
			name: kp.name,
			location_room: kp.location_room ?? undefined
		}));
		await generateEngravingSheetPdf(
			{
				project: resolveProjectName(ctx),
				unit: ctx.unit,
				keypads: engravingKeypads,
				keysByKeypad,
				roomsById
			},
			`${ctx.unit.name}-engraving-sheet.pdf`
		);
		showSuccess('Engraving sheet downloaded');
	} catch (error) {
		showError(getErrorMessage(error) || 'Failed to generate engraving sheet');
	} finally {
		setLoading(false);
	}
}

export async function downloadAddressSchedule() {
	const ctx = await unitContext();
	if (!ctx) return;

	setLoading(true);
	clearError();
	try {
		const base = buildExportData(ctx.domainGraph);
		const { addressScheduleCSV, downloadTextFile } = await import('./utils/engravingExport');
		const roomsById = new Map(base.rooms.map((r) => [r.id, r.name]));
		const rows = [];
		for (const rcu of base.rcus) {
			rows.push({
				device_type: 'RCU',
				room: '—',
				branch: 'main',
				address: '—',
				model: `RCU-${rcu.channel_count}CH`
			});
		}
		for (const din of base.dinModules) {
			rows.push({
				device_type: 'DIN',
				room: '—',
				branch: 'main',
				address: din.address,
				model: din.module_type
			});
		}
		for (const kp of base.keypads) {
			const roomId = kp.location_room;
			rows.push({
				device_type: kp.type === 'keycard' ? 'Keycard' : 'Keypad',
				room: roomId != null ? (roomsById.get(roomId) ?? '—') : '—',
				branch: kp.cat6_branch ?? 1,
				address: '—',
				model: `KP-${kp.keys?.length ?? 0}BTN`
			});
		}
		downloadTextFile(addressScheduleCSV(rows), `${ctx.unit.name}-address-schedule.csv`);
		showSuccess('Address schedule downloaded');
	} catch (error) {
		showError(getErrorMessage(error) || 'Failed to export address schedule');
	} finally {
		setLoading(false);
	}
}
